import { type FC, useCallback, useEffect } from 'react';
import { Provider } from 'react-redux';
import {
  AssistantRuntimeProvider,
  ChatModelAdapter,
  useLocalRuntime,
} from '@assistant-ui/react';
import { store } from './store';
import { SessionManager } from './utils/sessionManager';
import { CHATBOT_CONSTANTS, MESSAGE_ROLES } from './constants';
import type { SessionAwareMessage } from './types/session';
import {
  createErrorResponse,
  logError,
  getErrorMessage,
} from './utils/errorHandler';

interface PublicChatbotRuntimeProviderProps {
  children: React.ReactNode;
}

/**
 * Inner provider that uses Redux hooks (must be inside Redux Provider)
 */
const ChatRuntimeProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  /**
   * Handles normal chat flow
   */
  const handleNormalChatFlow = async (
    messages: any[],
    abortSignal: AbortSignal,
  ) => {
    const lastUserMessage = messages
      .filter((message) => message.role === MESSAGE_ROLES.USER)
      .slice(-1);

    // Get fresh sessionId for each request
    const sessionId =
      SessionManager.getCurrentSessionId() ||
      SessionManager.getOrCreateSession();

    try {
      // Normal chat flow
      const result = await fetch(
        '/apps/public-websearch-chatbot-client/api/v1/chat',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chatId: sessionId,
            messages: lastUserMessage,
          }),
          signal: abortSignal,
        },
      );

      // Handle HTTP status errors immediately with direct categorization
      if (!result.ok) {
        logError(
          new Error(`Chat API returned ${result.status}: ${result.statusText}`),
          'chat-http-error',
          { status: result.status, statusText: result.statusText, sessionId },
        );

        // Categorize HTTP errors directly by status code
        if (result.status >= 500) {
          return createErrorResponse(
            getErrorMessage('server', 'chat'),
            sessionId,
          );
        }
        if (result.status === 401 || result.status === 403) {
          return createErrorResponse(
            getErrorMessage('auth', 'chat'),
            sessionId,
          );
        }
        return createErrorResponse(
          getErrorMessage('unknown', 'chat'),
          sessionId,
        );
      }

      // Handle JSON parsing separately - malformed JSON is a server problem
      let data;
      try {
        data = await result.json();
      } catch (parseError) {
        logError(parseError, 'chat-json-parse-error', {
          sessionId,
          responseStatus: result.status,
        });

        // JSON parsing errors = server sent malformed data = server error
        return createErrorResponse(
          getErrorMessage('server', 'chat'),
          sessionId,
        );
      }

      const content =
        data.messages
          ?.filter((message: any) => message.role === MESSAGE_ROLES.ASSISTANT)
          .flatMap((message: any) => message.content || []) || [];

      let status = data?.messages?.[0]?.status;

      // Handle empty content scenario - keep generic message as requested
      if (!content.length) {
        status = {
          error: 'Sorry, something went wrong!',
          reason: 'error',
          type: 'incomplete',
        };
      }

      return {
        content,
        status,
        // Add sessionId to assistant response metadata
        metadata: {
          custom: { sessionId },
        },
      };
    } catch (error) {
      // Handle AbortError gracefully - execution context tells us it's user cancellation
      if (error instanceof Error && error.name === 'AbortError') {
        return createErrorResponse(getErrorMessage('abort', 'chat'), sessionId);
      }

      // Everything else that lands here from fetch() is a network error
      logError(error, 'chat-network-error', {
        sessionId,
        lastUserMessageLength: lastUserMessage.length,
      });

      return createErrorResponse(getErrorMessage('network', 'chat'), sessionId);
    }
  };

  const PublicChatbotModelAdapter: ChatModelAdapter = {
    async run({ messages, abortSignal }) {
      return handleNormalChatFlow(messages, abortSignal);
    },
  };

  const runtime = useLocalRuntime(PublicChatbotModelAdapter);

  /**
   * Restores sessionId metadata to messages loaded from storage
   *
   * @param {any} savedMessages - The saved messages data structure containing messages array
   * @returns {boolean} True if sessionIds were successfully restored, false otherwise
   */
  const restoreSessionIds = useCallback(
    (savedMessages: any) => {
      if (!savedMessages?.messages || !Array.isArray(savedMessages.messages)) {
        return false;
      }

      const messages = runtime.thread.getState().messages;

      // Ensure we have the expected number of messages
      if (messages.length !== savedMessages.messages.length) {
        return false;
      }

      let restored = false;
      savedMessages.messages.forEach((savedMsg: any, index: number) => {
        const savedSessionId = savedMsg?.message?.metadata?.custom?.sessionId;

        if (savedSessionId && messages[index] && index < messages.length) {
          const currentMessage = messages[index];

          // Only restore if not already set
          if (!currentMessage.metadata?.custom?.sessionId) {
            try {
              const sessionAwareMessage = currentMessage as SessionAwareMessage;
              sessionAwareMessage.metadata.custom = {
                ...sessionAwareMessage.metadata.custom,
                sessionId: savedSessionId,
              };

              restored = true;
            } catch (error) {
              console.warn('Failed to restore sessionId for message:', error);
            }
          }
        }
      });

      return restored;
    },
    [runtime],
  );

  const loadSavedMessages = useCallback(async () => {
    const savedMessages = SessionManager.loadMessages();

    if (!savedMessages) {
      return;
    }

    try {
      runtime.thread.import(savedMessages);

      // Wait for import to complete and attempt restoration
      const attemptRestore = async (
        maxAttempts = CHATBOT_CONSTANTS.MAX_RESTORE_ATTEMPTS,
      ) => {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          await new Promise((resolve) =>
            setTimeout(
              resolve,
              CHATBOT_CONSTANTS.BASE_RESTORE_DELAY_MS * attempt,
            ),
          );

          // Check if messages are actually loaded before attempting restoration
          const currentMessages = runtime.thread.getState().messages;
          if (currentMessages.length === 0 && attempt < maxAttempts) {
            continue; // Runtime not ready yet
          }

          if (restoreSessionIds(savedMessages)) {
            return; // Success
          }

          if (attempt === maxAttempts) {
            // Reduce warning to debug level since this is not critical for functionality
            console.debug(
              'SessionId restoration incomplete after',
              maxAttempts,
              'attempts - messages will still work',
            );
          }
        }
      };

      await attemptRestore();
    } catch (error) {
      console.warn('Failed to restore messages:', error);
    }
  }, [runtime, restoreSessionIds]);

  /**
   * Centralized session reset handler for all session reset triggers
   *
   * This function handles session resets triggered by various events as specified in the README:
   * - Inactivity timeout (1 hour default)
   * - API error conditions
   * - Browser tab close/restart
   *
   * The function performs two main operations:
   * 1. Resets the current session (generates new UUID via SessionManager)
   * 2. Reloads saved messages to maintain conversation continuity as archive context
   *
   * @example
   * ```typescript
   * // Automatically triggered by events:
   * window.dispatchEvent(new CustomEvent('chatbot-inactivity-timeout'));
   * window.dispatchEvent(new CustomEvent('chatbot-api-error'));
   * ```
   *
   * @remarks
   * - Creates new session ID while preserving existing messages as archive
   * - Enables users to see previous conversation context as read-only
   * - Ensures clean session boundaries for new conversations
   * - Integrates with SessionManager for consistent session lifecycle management
   */
  const handleSessionReset = useCallback(() => {
    // First, save current messages before resetting session
    try {
      const exportedMessages = runtime.thread.export();
      SessionManager.saveMessages(exportedMessages);
    } catch (error) {
      console.warn('Failed to save messages before session reset:', error);
    }

    // Reset session in SessionManager (creates new UUID)
    SessionManager.resetSession();

    // Reload saved messages to maintain continuity (archive context)
    void loadSavedMessages();
  }, [loadSavedMessages, runtime]);

  // Listen for session reset triggers
  useEffect(() => {
    // Register all session reset event listeners as per README requirements
    window.addEventListener('chatbot-inactivity-timeout', handleSessionReset);
    window.addEventListener('chatbot-api-error', handleSessionReset);

    return () => {
      window.removeEventListener(
        'chatbot-inactivity-timeout',
        handleSessionReset,
      );
      window.removeEventListener('chatbot-api-error', handleSessionReset);
    };
  }, [handleSessionReset]);

  // Load messages when loadSavedMessages function updates
  useEffect(() => {
    void loadSavedMessages();
  }, [loadSavedMessages]);

  // Centralized message saving logic
  const saveMessages = useCallback(() => {
    try {
      const exportedMessages = runtime.thread.export();
      SessionManager.saveMessages(exportedMessages);
    } catch (error) {
      console.warn('Failed to save messages:', error);
    }
  }, [runtime]);

  // Set up message saving handlers with proper cleanup
  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let isCleaningUp = false;

    const handleBeforeUnload = () => {
      if (!isCleaningUp) {
        // Always save messages, even if session is cleared
        // Messages should persist across modal close/open cycles
        saveMessages();
      }
    };

    const handleModalClosing = () => {
      if (!isCleaningUp) {
        saveMessages();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('chatbot-modal-closing', handleModalClosing);

    // Set up interval with null check
    intervalId = setInterval(() => {
      if (!isCleaningUp) {
        saveMessages();
      }
    }, CHATBOT_CONSTANTS.AUTO_SAVE_INTERVAL_MS);

    return () => {
      isCleaningUp = true;

      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('chatbot-modal-closing', handleModalClosing);

      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }

      // Always save on cleanup to ensure messages persist
      try {
        saveMessages();
      } catch (error) {
        console.warn('Error saving messages during cleanup:', error);
      }
    };
  }, [saveMessages]);

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      {children}
    </AssistantRuntimeProvider>
  );
};

/**
 * Main provider that wraps the app with Redux store and chat runtime
 */
export const PublicChatbotRuntimeProvider: FC<
  PublicChatbotRuntimeProviderProps
> = ({ children }) => {
  return (
    <Provider store={store}>
      <ChatRuntimeProvider>{children}</ChatRuntimeProvider>
    </Provider>
  );
};
